import { usePrintEditorStore } from '../state/usePrintEditorStore';
import { PanelSection, T } from '../ui/figma';

/* ------------------------------------------------------------------ *
 *  Shortcuts — the editor's keys and canvas gestures, in one place.
 *
 *  Every row here is a real binding: keys come from CanvasViewport's keydown
 *  handler and PrintTemplateEditor's editor-level one, gestures from the canvas
 *  pointer handlers. Add a binding there → add its row here, or the sheet starts
 *  lying (which is worse than not having it).
 * ------------------------------------------------------------------ */

const IS_MAC = typeof navigator !== 'undefined'
    && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
// Modifiers are SPELLED OUT, not drawn as ⌘ ⇧ ⌥. The symbols are Mac keycaps and
// mean nothing to anyone else — a sheet exists to be read at a glance, and "Shift"
// is never in doubt.
const MOD = IS_MAC ? 'Cmd' : 'Ctrl';
const ALT = IS_MAC ? 'Opt' : 'Alt';
const SHIFT = 'Shift';
const DEL = IS_MAC ? 'Delete' : 'Del';

/** A key chip; `soft` marks a gesture ("drag", "double-click") rather than a key. */
function Key({ children, soft = false }) {
    return (
        <span
            className={`h-[18px] px-1.5 flex items-center justify-center rounded-[4px] text-[10px] leading-none
                whitespace-nowrap ${soft
                    ? `${T.textSoft} border ${T.border}`
                    : `${T.inputBg} ${T.text} font-medium`}`}
        >
            {children}
        </span>
    );
}

function Shortcut({ label, keys }) {
    return (
        <div className="flex items-center gap-2 min-h-[24px] py-0.5">
            <span className={`flex-1 min-w-0 text-[11px] ${T.text}`}>{label}</span>
            <span className="shrink-0 flex items-center gap-1">
                {keys.map((k) => (
                    <Key key={String(k.k ?? k)} soft={typeof k === 'object' && k.soft}>
                        {typeof k === 'object' ? k.k : k}
                    </Key>
                ))}
            </span>
        </div>
    );
}

const g = (k) => ({ k, soft: true }); // gesture chip

const ESSENTIALS = [
    ['Save', [MOD, 'S']],
    ['Undo', [MOD, 'Z']],
    ['Redo', [MOD, SHIFT, 'Z']],
    ['Duplicate', [MOD, 'D']],
    ['Copy', [MOD, 'C']],
    ['Cut', [MOD, 'X']],
    ['Paste', [MOD, 'V']],
    ['Select all', [MOD, 'A']],
    ['Delete selection', [DEL]],
    ['Deselect / step out', ['Esc']],
];

const TOOLS = [
    ['Move', ['V']],
    ['Hand (pan)', ['H']],
    ['Text', ['T']],
    ['Rectangle', ['R']],
    ['Ellipse', ['O']],
    ['Line', ['L']],
    ['Arrow', [SHIFT, 'L']],
    ['Frame', ['F']],
    ['Pen', ['P']],
    ['Pencil', [SHIFT, 'P']],
];

/** Only shown while a drawing tool is armed — they mean nothing otherwise. */
const DRAWING = [
    ['Place a corner point', [g('click')]],
    ['Place a curve point', [g('click-drag')]],
    ['Close the shape', [g('click the first point')]],
    ['Finish an open path', ['Enter']],
    ['Undo the last point', ['Backspace']],
    ['Abandon the path', ['Esc']],
    ['Leave the tool', ['Esc', 'Esc']],
];

const VIEW = [
    ['Zoom in', [MOD, '+']],
    ['Zoom out', [MOD, '−']],
    ['Zoom to 100%', [MOD, '1']],
    ['Zoom to fit', [MOD, '0']],
    ['Pan the canvas', ['Space', g('drag')]],
    ['Pan from empty canvas', [ALT, g('drag')]],
    ['Zoom at the pointer', [MOD, g('scroll')]],
    ['Scroll sideways', [SHIFT, g('scroll')]],
    ['Show / hide sidebar', [MOD, '\\']],
    // Preview is entered from the play button in the panel's top row; Esc is the
    // only key it answers to.
    ['Leave preview', ['Esc']],
];

const ARRANGE = [
    ['Group', [MOD, 'G']],
    ['Ungroup', [MOD, SHIFT, 'G']],
    ['Bring to front', [']']],
    ['Send to back', ['[']],
    // Ctrl, not Cmd, on every platform — that is what the canvas listens for.
    ['Use as mask', ['Ctrl', 'M']],
    ['Flip horizontal', [SHIFT, 'H']],
    ['Flip vertical', [SHIFT, 'V']],
    ['Hide / show', [MOD, SHIFT, 'H']],
    ['Lock / unlock here (admin)', [MOD, SHIFT, 'L']],
    ['Select all locked layers', [MOD, 'L']],
    ['Select all unlocked layers', [MOD, 'U']],
    // Ctrl, not Cmd, on a Mac too: Cmd+Option+L is the browser's Downloads page.
    ['Lock / unlock for the user', ['Ctrl', ALT, 'L']],
    ['Duplicate frame', [MOD, SHIFT, 'D']],
    ['Nudge 1 px', ['↑', '↓', '←', '→']],
    ['Nudge 10 px', [SHIFT, '↑↓←→']],
];

const LAYERS = [
    ['Select a range', [SHIFT, g('click')]],
    ['Add / remove one', [MOD, g('click')]],
    ['Rename', [g('double-click')]],
];

const CANVAS = [
    ['Menu for what you clicked', [g('right-click')]],
    // The answer to "a photo covers the shape I want": the menu lists every layer
    // under the pointer, and dragging inside the selection moves the selection
    // even when something bigger is painted over it.
    ['Pick a layer underneath', [g('right-click'), g('Select layer')]],
    ['Move the selected layer', [g('drag inside it')]],
    // Blank artboard included — a frame is moved by its name, never its body.
    ['Select several layers', [g('drag on empty space')]],
    ['Move a frame', [g('drag its name')]],
    ['Edit a text layer', [g('double-click')]],
    ['Crop an image', [g('double-click')]],
    ['Step into a group', [g('double-click')]],
    ['Duplicate', [ALT, g('drag')]],
    ['Drag on one axis', [SHIFT, g('drag')]],
    ['Resize in proportion', [SHIFT, g('handle')]],
    ['Rotate', [g('past a corner')]],
    ['Duplicate a frame', [ALT, g('frame name')]],
    ['Finish cropping', ['Esc']],
];

function Group({ title, rows, note }) {
    return (
        <PanelSection title={title}>
            {rows.map(([label, keys]) => <Shortcut key={label + keys.join()} label={label} keys={keys} />)}
            {note && <p className={`mt-1.5 text-[10px] leading-snug ${T.textFaint}`}>{note}</p>}
        </PanelSection>
    );
}

export default function ShortcutsPanel() {
    const mode = usePrintEditorStore((s) => s.mode);
    const admin = mode === 'admin';
    return (
        <div>
            <Group title="Essentials" rows={ESSENTIALS} />
            {/* Insert tools and grouping are admin-only, so their keys are too. */}
            {admin && <Group title="Tools" rows={TOOLS} />}
            {admin && (
                <Group
                    title="Pen and pencil"
                    rows={DRAWING}
                    note="The pencil needs none of these — hold the button down and draw."
                />
            )}
            <Group title="View" rows={VIEW} />
            {admin && <Group title="Arrange" rows={ARRANGE} />}
            <Group title="Layers panel" rows={LAYERS} />
            <Group
                title="On canvas"
                // End users get no ⌥-drag copy (duplicateInPlace refuses in user mode)
                // and no frames or groups to work on — so those rows would be lies.
                rows={admin ? CANVAS : CANVAS.filter(([l]) => !/group|frame|duplicate/i.test(l))}
                note={`${SHIFT} is read continuously — press or release it mid-drag. ${ALT} has to `
                    + 'be held before the drag starts, since that is when the copy is made.'}
            />
        </div>
    );
}
